import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'insidePanama_comfort';
const EVENT = 'comfort-change';
const ATTR = 'data-comfort';

// Mode confort : binary 'on' | 'off'. Pensionado / accessibilité.
// Quand activé, pose data-comfort="on" sur <html> ; le CSS global
// (src/index.css) gère le scaling typo, contraste, animations off.
// FOUC prévenu par un mini-snippet inline dans index.html.

function getStored() {
  if (typeof window === 'undefined') return 'off';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'on' || raw === 'off') return raw;
  } catch { /* private mode */ }
  return 'off';
}

function apply(state) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (state === 'on') root.setAttribute(ATTR, 'on');
  else root.removeAttribute(ATTR);
}

/**
 * Reactive comfort-mode hook. Sister of useTheme(), same persistence
 * pattern (localStorage + custom event + cross-tab sync via 'storage').
 *
 * Returns :
 *   - `enabled`    : boolean
 *   - `toggle`     : flip on↔off
 *   - `setEnabled` : explicit setter (true|false)
 */
export function useComfort() {
  const [state, setState] = useState(() => getStored());

  useEffect(() => { apply(state); }, [state]);

  useEffect(() => {
    const onCustom = () => setState(getStored());
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setState(getStored()); };
    window.addEventListener(EVENT, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setEnabled = useCallback((next) => {
    const v = next ? 'on' : 'off';
    try { window.localStorage.setItem(STORAGE_KEY, v); } catch { /* */ }
    setState(v);
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  const toggle = useCallback(() => setEnabled(state !== 'on'), [state, setEnabled]);

  return { enabled: state === 'on', toggle, setEnabled };
}

// FOUC-prevention snippet — inlined in index.html before bundle loads.
// Exported for documentation only; the actual JS lives in <head>.
export const FOUC_COMFORT_SCRIPT = `(function(){try{if(localStorage.getItem('${STORAGE_KEY}')==='on'){document.documentElement.setAttribute('${ATTR}','on');}}catch(e){}})();`;
